import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
  useRef,
} from "react";
import { InputGroup } from "react-bootstrap";
import Select from "react-select";
import { ArrayHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { Item } from "models/ECF/Item";
import { ItemManager } from "managers";
import ToolTip from "components/ToolTip/ToolTip";
import { GridView } from "components/GridView/GridView";
import IconButton from "components/IconButton/IconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import { AutoComplete, SvgComponent } from "components";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Simplebar = lazy(() => import("components/Simplebar/Simplebar"));
const ItemEdit = lazy(() => import("views/Item/ItemEdit"));
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import {
  buttonStyle,
  controlsProportion,
  initialWhere,
  initialWhereModal,
} from "..";
import "../Lookup.style.scss";

export const ItemLookup = forwardRef(
  (
    {
      isDisabled,
      idSecList = [],
      isInput = false,
      isMultiselect = false,
      onChange = () => { },
      onMultiselectChange = () => { },
      initialValue = { label: "", value: "" },
      initialValueList = [],
      limpiar = false,
      required = false,
      loadOnShowModal = false,
      loadValue = false,
      whereFijo = {},
    },
    ref
  ) => {
    const isMounted = useRef(true);
    const [showCanvas, setShowCanvas] = useState(false);
    const [showItemEditCanvas, setShowItemEditCanvas] = useState(false);
    const [itemListModal, setItemListModal] = useState([]); // Changed name
    const [whereModal, setWhereModal] = useState(initialWhereModal);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [selectedValue, setSelectedValue] = useState({
      label: "",
      value: "",
      Nombre: "",
      IndicadorFacturacion: "",
      IndicadorBienOServicio: "",
      PrecioUnitario: 0,
      Cantidad: ""
    });
    const [multiSelectedValue, setMultiSelectedValue] = useState([]);

    const ESTATUS_LIST = [
      { value: "", label: "Todos" },
      ...(Item.ESTATUS_LIST || []),
    ];
    const INDICADOR_FACTURACION_FILTER_LIST = [{ value: null, label: "Todos" }, ...Item.INDICADOR_FACTURACION_LIST];
    const INDICADOR_BIEN_O_SERVICIO_FILTER_LIST = [{ value: null, label: "Todos" }, ...Item.INDICADOR_BIEN_O_SERVICIO_LIST];

    const { handleException } = useExceptionHandler();
    const { setPaginationValuesWhere } = usePaginationValuesWhere();

    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (limpiar) {
        handleMultiselectChange([]);
      }
    }, [limpiar]);

    useEffect(() => {
      if (!loadOnShowModal) {
        buscar();
      }
    }, [whereModal, JSON.stringify(whereFijo)]);

    useEffect(() => {
      if (
        initialValue &&
        initialValue.value &&
        initialValue.value !== selectedValue.value
      ) {
        if (loadValue && !initialValue.label) buscarPorId(initialValue.value);
        else setSelectedValue(initialValue);
      }
    }, [initialValue.value]);

    useEffect(() => {
      if (initialValueList && initialValueList.length > 0) {
        setMultiSelectedValue(initialValueList);
      }
    }, [initialValueList.length]);

    const handleMultiselectChange = (value) => {
      setMultiSelectedValue(value ? value : []);
      onMultiselectChange(value ? value : []);
    };

    const handleSelectChange = (value) => {
      setSelectedValue(value ? value : {});
      onChange(value ? value : {});
    };

    function buscar() {
      const whereWithPaginationValues = {
        Estatus: Item.DEFAULT_ESTATUS_VALUE,
        ...whereFijo,
        ...whereModal,
        ...pagination,
        IdSecList: idSecList,
      };
      ItemManager
        .listado(whereWithPaginationValues)
        .then((res) => {
          if (isMounted.current) {
            setPaginationValuesWhere(res.Headers, pagination, setPagination);
            const newList = res.Data.map(item => ({
              ...item,
              IndicadorBienOServicioLabel: Item.getIndicadorBienServicioLabel(item.IndicadorBienOServicio),
              IndicadorFacturacionLabel: Item.getFacturacionLabel(item.IndicadorFacturacion),
            }));
            setItemListModal(newList);
          }
        })
        .catch((err) => handleException(err));
    }

    const buscarPorId = (idsec) => {
      ItemManager
        .buscarPorId({ IdSec: idsec, EsLookup: true })
        .then((res) => {
          setSelectedValue({ label: res.label, value: res.value });
        })
        .catch((err) => handleException(err));
    };

    const handleCerrar = () => {
      setShowCanvas(false);
      setWhereModal(initialWhereModal);
    };

    const onPageIndexChanging = (pageNumber) => {
      setPagination((prevstate) => {
        return { ...prevstate, IndiceDePagina: pageNumber - 1 };
      });
      setWhereModal({ ...whereModal });
    };

    const getSuggestions = async (value) => {
      const inputValue = value.trim().toLowerCase();
      const res = await ItemManager
        .listado({ // Manager call
          ...initialWhere,
          ...whereFijo,
          Nombre: inputValue,
          IdSecList: idSecList,
          EsLookup: true,
        });
      return res.Data;
    };

    const addToList = (newItem) => {
      setMultiSelectedValue((prev) => (prev ? [...prev, newItem] : [newItem]));
    };

    useImperativeHandle(ref, () => ({
      addToList,
      openItemEdit: () => setShowItemEditCanvas(true)
    }));

    const renderInputContent = () => (
      <>
        <div style={{ width: isInput ? controlsProportion.inputGroup : controlsProportion.input }}>
          <AutoComplete
            required={required}
            isDisabled={isDisabled}
            getList={getSuggestions}
            noBorderRadius={isInput}
            isMultiselect={isMultiselect}
            placeholder={isMultiselect ? "Items" : "Item / Artículo"}
            parentValue={isMultiselect ? multiSelectedValue : selectedValue}
            setParentValue={
              isMultiselect ? handleMultiselectChange : handleSelectChange
            }
          />
        </div>
        {!isDisabled && (
          <InputGroup.Append style={{ width: controlsProportion.button }}>
            <AnimatedIconButton
              size="40px"
              height="18"
              width="18"
              message="Seleccionar Item"
              style={buttonStyle}
              disabled={isDisabled}
              iconName="search"
              onClick={() => {
                setShowCanvas(true);
                setTimeout(() => {
                  buscar();
                }, 200);
              }}
            />
          </InputGroup.Append>
        )}
      </>
    );

    return (
      <>
        {!isInput ? (
          <InputGroup className="w-100">
            {renderInputContent()}
          </InputGroup>
        ) : (
          renderInputContent()
        )}

        {showItemEditCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <ItemEdit
              IdSec={undefined}
              showCanvas={showItemEditCanvas}
              setShowCanvas={setShowItemEditCanvas}
              showLoading={() => { }}
              hideLoading={() => { }}
              updateList={buscar}
              esCreacionExterna={true}
              onCreated={(res) => {
                handleSelectChange({
                  label: res.Nombre,
                  value: res.IdSec,
                  Nombre: res.Nombre,
                  IndicadorFacturacion: res.IndicadorFacturacion,
                  IndicadorBienOServicio: res.IndicadorBienOServicio,
                  PrecioUnitario: res.PrecioUnitario,
                  TipoISC: res.TipoISC,
                  OtroImpuesto: res.OtroImpuesto,
                  Cantidad: 1,
                });
              }}
            />
          </Suspense>
        )}

        {showCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <Simplebar
              backdrop={true}
              placement={"end"}
              portal={false}
              size="lm"
              useLoading={true}
              visible={showCanvas}
              setShow={(e) => setShowCanvas(e)}
              title="Seleccionar Item"
              btnLeft={{ show: false, func: () => { } }}
              btnRight={{ show: false, func: () => { } }}
            >
              <GridView
                pagination={pagination}
                setPagination={setPagination}
                allowPaging={pagination.EsPaginable}
                pageSize={pagination.CantidadPorPagina}
                pageIndex={pagination.IndiceDePagina}
                lastPageNumber={pagination.PaginasTotal}
                dataSource={itemListModal}
                onPageIndexChanging={onPageIndexChanging}
                where={whereModal}
                setWhere={setWhereModal}
              >
                <GridView.Column
                  isSticky
                  bodyRenderer={(row) => (
                    <div className="float-right">
                      <IconButton
                        onClick={() => {
                          let values = multiSelectedValue || [];

                          if (isMultiselect) {
                            handleMultiselectChange(
                              ArrayHelper.removeObjectsDuplicated(
                                [
                                  ...values,
                                  {
                                    label: row.Nombre,
                                    value: row.IdSec,
                                    Nombre: row.Nombre,
                                    IndicadorFacturacion: row.IndicadorFacturacion,
                                    IndicadorBienOServicio: row.IndicadorBienOServicio,
                                    PrecioUnitario: row.PrecioUnitario,
                                    TipoISC: row.TipoISC,
                                    OtroImpuesto: row.OtroImpuesto
                                  }
                                ],
                                ["value", "label"]
                              )
                            );
                          } else {
                            handleSelectChange({
                              label: row.Nombre,
                              value: row.IdSec,
                              Nombre: row.Nombre,
                              IndicadorFacturacion: row.IndicadorFacturacion,
                              IndicadorBienOServicio: row.IndicadorBienOServicio,
                              PrecioUnitario: row.PrecioUnitario,
                              TipoISC: row.TipoISC,
                              OtroImpuesto: row.OtroImpuesto,
                              Cantidad: 1
                            });
                          }
                          handleCerrar();
                        }}
                      >
                        <ToolTip message="Seleccionar">
                          <div>
                            <SvgComponent height="20" width="20" iconName="check" />
                          </div>
                        </ToolTip>
                      </IconButton>
                    </div>
                  )}
                />
                <GridView.Column
                  headerText="Id"
                  dataField="IdSec"
                  sortExpression="IdSec"
                  width="110px"
                />
                <GridView.Column
                  headerText="Nombre"
                  dataField="Nombre"
                  sortExpression="Nombre"
                  width="200px"
                />
                <GridView.Column
                  headerText="Bien o Servicio"
                  dataField="IndicadorBienOServicioLabel"
                  sortExpression="IndicadorBienOServicio"
                  width="150px"
                  filterRenderer={() => {
                    return (
                      <Select
                        className="react-select primary"
                        options={INDICADOR_BIEN_O_SERVICIO_FILTER_LIST}
                        value={INDICADOR_BIEN_O_SERVICIO_FILTER_LIST.filter(function (option) {
                          return (
                            option.value ===
                            (whereModal.IndicadorBienOServicio ?? INDICADOR_BIEN_O_SERVICIO_FILTER_LIST[0].value)
                          );
                        })}
                        name="IndicadorBienOServicio"
                        onChange={(selectedOption) => {
                          setWhereModal({
                            ...whereModal,
                            IndicadorBienOServicio: selectedOption.value,
                          });
                        }}
                      />
                    );
                  }}
                />
                <GridView.Column
                  headerText="Precio Unitario"
                  dataField="PrecioUnitario"
                  sortExpression="PrecioUnitario"
                  width="150px"
                  number={true}
                />
                <GridView.Column
                  headerText="Tasa ITBIS"
                  dataField="IndicadorFacturacionLabel"
                  sortExpression="IndicadorFacturacion"
                  width="200px"
                  filterRenderer={() => {
                    return (
                      <Select
                        className="react-select primary"
                        options={INDICADOR_FACTURACION_FILTER_LIST}
                        value={INDICADOR_FACTURACION_FILTER_LIST.filter(function (option) {
                          return (
                            option.value ===
                            (whereModal.IndicadorFacturacion ?? INDICADOR_FACTURACION_FILTER_LIST[0].value)
                          );
                        })}
                        name="IndicadorFacturacion"
                        onChange={(selectedOption) => {
                          setWhereModal({
                            ...whereModal,
                            IndicadorFacturacion: selectedOption.value,
                          });
                        }}
                      />
                    );
                  }}
                />
              </GridView>
            </Simplebar>
          </Suspense>
        )}
      </>
    );
  }
);